// discos.js — Radio · Lanzamientos y la página del disco.
//
// Contrato: `listen.astra.fm/docs/DISCOS_WEB_INSTRUCCIONES.md` (19.10.26).
// Endpoint: https://listen.astra.fm/discos/lanzamientos.json
//
// 🔴 **Solo tienen página los discos de esta lista**: los de este año que suenan
// en Astra. Cualquier otro `discos/<slug>.json` da 404 (§1). Así que antes de
// enlazar un disco a su página hay que encontrarlo aquí; un slug fabricado a
// partir del título llevaría a una página que no existe.
//
// ⚠️ El fichero mide unos 140 KB y se regenera de noche y cuando Pablo toca el
// Studio (§1). Se baja una vez por visita, como mucho cada diez minutos, y en
// paralelo a lo que suena: que tarde o falle no puede retrasar la emisión.

import { LISTEN } from "./host";
import { artistSlug } from "./editorialArtists";
import { i18n } from "@/i18n";
import { IDIOMA } from "@/i18n/idioma";

const t = (...a) => i18n.global.t(...a);

const URL = `${LISTEN}/discos/lanzamientos.json`;
const TTL = 10 * 60 * 1000;

let cache = null;
let cacheAt = 0;
let enVuelo = null;

/**
 * Cómo se compara un título: sin mayúsculas, sin tildes y sin puntuación. La
 * emisión y la ficha escriben el mismo disco con comillas o signos distintos.
 */
function clave(texto) {
  return String(texto || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}

const llave = (artista, titulo) => `${clave(artista)}|${clave(titulo)}`;

/**
 * La lista de la pestaña, tal cual la manda el servidor, y un índice por
 * artista y título para encontrar un disco desde la emisión o desde la ficha.
 * Si falla, una lista vacía: sin ella ningún disco enlaza, que es lo prudente.
 */
export async function getDiscos() {
  const ahora = Date.now();
  if (cache && ahora - cacheAt <= TTL) return cache;
  if (enVuelo) return enVuelo;

  enVuelo = fetch(URL, { headers: { Accept: "application/json" } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null)
    .then((d) => {
      enVuelo = null;
      const discos = Array.isArray(d?.discos) ? d.discos : [];
      const indice = new Map();
      for (const disco of discos) {
        // Por el slug del artista, que es lo estable, y por su nombre, que es lo
        // que trae la emisión cuando no hay ficha.
        if (disco.artistSlug) indice.set(llave(disco.artistSlug, disco.titulo), disco);
        indice.set(llave(artistSlug(disco.artista || ""), disco.titulo), disco);
      }
      const resultado = { anio: d?.anio || null, destacado: d?.destacado || null, discos, indice };
      // Un fallo no se guarda: la próxima vez se vuelve a pedir.
      if (d) {
        cache = resultado;
        cacheAt = Date.now();
      }
      return resultado;
    });

  return enVuelo;
}

/**
 * El disco de la lista que corresponde a un artista y un título, o `null` si no
 * está: entonces no tiene página.
 *
 * @param {{ indice: Map }} lista  lo que devuelve `getDiscos()`
 * @param {string} artista  slug del artista o, si no hay, su nombre
 * @param {string} titulo
 */
export function buscaDisco(lista, artista, titulo) {
  if (!lista?.indice || !artista || !titulo) return null;
  return (
    lista.indice.get(llave(artista, titulo)) ||
    lista.indice.get(llave(artistSlug(artista), titulo)) ||
    null
  );
}

// ── La página de un disco ──

const fichas = new Map();

/**
 * `discos/<slug>.json`, o `null` si el disco no tiene página (404) o no llegó.
 * Se guarda lo que llega bien: volver a la misma página no la pide otra vez.
 */
export async function getDisco(slug) {
  if (!slug) return null;
  if (fichas.has(slug)) return fichas.get(slug);
  try {
    const r = await fetch(`${LISTEN}/discos/${encodeURIComponent(slug)}.json`, {
      headers: { Accept: "application/json" },
    });
    if (r.status === 404) {
      fichas.set(slug, null);
      return null;
    }
    if (!r.ok) return null;
    const d = await r.json();
    fichas.set(slug, d);
    return d;
  } catch {
    return null;
  }
}

/** La dirección de la página de un disco de la lista (Pablo, 19.10.26). */
export const rutaDelDisco = (d) => (d?.slug ? `/lanzamientos/${d.slug}` : null);

// «album» → «Álbum». El servidor manda `album` · `ep` · `single`, o `null`.
// El texto, en el idioma de la página (`radio.disco.tipo`).
const TIPOS = ["album", "ep", "single"];
export const tipoDe = (tipo) => {
  const clave = String(tipo || "").toLowerCase();
  return TIPOS.includes(clave) ? t(`radio.disco.tipo.${clave}`) : "";
};

// ── Fechas, en hora de Madrid ──
//
// 🔴 **Día · Semana · Mes los calcula la web sobre `fecha`, en hora de Madrid**
// (§3). `fecha` es un día sin hora («2026-10-16»), así que se compara como día
// de calendario; lo único que hace falta es saber qué día es HOY en Madrid, que
// no es el del reloj del navegador si quien mira está en otra zona.

/** Hoy en Madrid, como «AAAA-MM-DD». */
export function hoyEnMadrid(ahora = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Madrid",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(ahora);
}

// Un día de calendario como fecha UTC a mediodía: sumar y restar días sin que
// el cambio de hora mueva nada.
const aDia = (iso) => {
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12));
};
const aIso = (f) => f.toISOString().slice(0, 10);
const masDias = (iso, n) => {
  const f = aDia(iso);
  f.setUTCDate(f.getUTCDate() + n);
  return aIso(f);
};

// ⚠️ Tanda 3 de idiomas: los meses van en el idioma de la página. El español
// sale de estas tablas, carácter por carácter, como antes. Los otros tres llevan
// su abreviatura habitual (no la de tres letras de `i18n/fechas.js`, que es la de
// los chips en mayúsculas: «aoû» en francés no se escribe así en minúscula).
const MESES_CORTOS = {
  es: ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"],
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
  ca: ["gen", "febr", "març", "abr", "maig", "juny", "jul", "ag", "set", "oct", "nov", "des"],
  fr: ["janv", "févr", "mars", "avr", "mai", "juin", "juil", "août", "sept", "oct", "nov", "déc"],
};
const MESES_LARGOS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

/** El nombre del mes con mayúscula, `m` de 0 a 11: «Octubre», «October», «Octobre». */
function mesLargo(m) {
  if (IDIOMA === "es" || !IDIOMA) return MESES_LARGOS[m];
  const nombre = new Intl.DateTimeFormat(IDIOMA === "en" ? "en-GB" : IDIOMA, {
    month: "long",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(2026, m, 15)));
  return nombre.charAt(0).toUpperCase() + nombre.slice(1);
}

/** «2026-10-17» → «17 oct». */
export function diaCorto(iso) {
  const f = aDia(iso);
  const meses = MESES_CORTOS[IDIOMA] || MESES_CORTOS.es;
  return `${f.getUTCDate()} ${meses[f.getUTCMonth()]}`;
}

/** «2026-10-17» → «17 oct 2026». */
export const diaLargo = (iso) => `${diaCorto(iso)} ${aDia(iso).getUTCFullYear()}`;

/** La semana ISO de un día: número y lunes. */
function semanaDe(iso) {
  const f = aDia(iso);
  const dia = (f.getUTCDay() + 6) % 7; // lunes = 0
  const lunes = masDias(iso, -dia);
  // El jueves de la semana dice a qué año pertenece, y de ahí el número.
  const jueves = aDia(masDias(lunes, 3));
  const enero = new Date(Date.UTC(jueves.getUTCFullYear(), 0, 1, 12));
  const numero = Math.floor((jueves - enero) / 86400000 / 7) + 1;
  return { numero, lunes };
}

/**
 * Si un disco cuenta como NUEVO LANZAMIENTO: **30 días desde la fecha de
 * salida** (Pablo, 19.10.26). Si solo se sabe la de entrada en la radio
 * (`fechaTipo: "alta"`), no hay chip.
 */
export function esNuevo(d, hoy = hoyEnMadrid()) {
  if (!d?.fecha || d.fechaTipo !== "salida") return false;
  const fecha = String(d.fecha).slice(0, 10);
  return fecha <= hoy && fecha > masDias(hoy, -30);
}

/**
 * ADELANTO: el disco ya suena pero **todavía no ha salido** (Pablo, 04.11.26).
 * El día que sale pasa a NUEVO. `salida` es la fecha de salida, `AAAA-MM-DD`.
 */
export function esAdelanto(salida, hoy = hoyEnMadrid()) {
  return !!salida && String(salida).slice(0, 10) > hoy;
}

/**
 * La descripción de un disco como HTML: los títulos van en cursiva con
 * `*Título*` (contrato § 4). Se escapa todo y luego se convierte solo eso, así
 * que nada más del texto puede acabar siendo HTML.
 */
export function descripcionHtml(texto) {
  if (!texto) return "";
  return String(texto)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/\*([^*\n]+)\*/g, "<em>$1</em>");
}

/** La misma descripción en texto plano, sin los asteriscos. */
export const descripcionPlana = (texto) => String(texto || "").replace(/\*/g, "");

// ── El calendario de la pestaña (20.10.26) ──
//
// `Pattern/CalendarioMes`: una fila por semana, de lunes a domingo, y el mes
// entero abajo. Entrega `2026-10-20-radio-lanzamientos-calendario.md`.

/** El periodo de una semana a partir de su lunes, con el rótulo de la barra. */
export function periodoSemana(lunes) {
  const { numero } = semanaDe(lunes);
  const domingo = masDias(lunes, 6);
  const mismoMes = lunes.slice(0, 7) === domingo.slice(0, 7);
  const tramo = mismoMes
    ? `${aDia(lunes).getUTCDate()}–${diaCorto(domingo)}`
    : `${diaCorto(lunes)}–${diaCorto(domingo)}`;
  return { tipo: "semana", desde: lunes, hasta: domingo, rotulo: t("radio.lanzamientos.semana", { n: numero, tramo }) };
}

/** El periodo de un mes («2026-10»), con su rótulo: «Octubre 2026». */
export function periodoMes(mes) {
  const [y, m] = mes.split("-").map(Number);
  const desde = `${mes}-01`;
  const hasta = aIso(new Date(Date.UTC(y, m, 0, 12)));
  return { tipo: "mes", mes, desde, hasta, rotulo: `${mesLargo(m - 1)} ${y}` };
}

/** «2026-10» → «Octubre». */
export const nombreDelMes = (mes) => mesLargo(Number(mes.split("-")[1]) - 1);

/** El lunes de la semana de un día. */
export const lunesDe = (iso) => semanaDe(iso).lunes;

/** El mes de al lado: `masMeses("2026-10", -1)` → «2026-09». */
export function masMeses(mes, n) {
  const [y, m] = mes.split("-").map(Number);
  const f = new Date(Date.UTC(y, m - 1 + n, 1, 12));
  return aIso(f).slice(0, 7);
}

/**
 * Las semanas que tocan un mes, de lunes a domingo: cinco o seis filas.
 * Cada una con su número ISO, su lunes y sus siete días.
 */
export function semanasDelMes(mes) {
  const primero = `${mes}-01`;
  const { hasta } = periodoMes(mes);
  const semanas = [];
  for (let lunes = semanaDe(primero).lunes; lunes <= hasta; lunes = masDias(lunes, 7)) {
    semanas.push({
      numero: semanaDe(lunes).numero,
      lunes,
      dias: Array.from({ length: 7 }, (_, i) => {
        const iso = masDias(lunes, i);
        return { iso, dia: aDia(iso).getUTCDate(), enMes: iso.slice(0, 7) === mes };
      }),
    });
  }
  return semanas;
}
